import React from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RagBadge } from "@/components/common/RagBadge";
import { ProgressBar } from "@/components/common/Progress";
import { canManageTeam, PERIOD_STATE, VERIFY_STATE } from "@/lib/workspace";
import { RAG, fmtValue } from "@/lib/format";

function Column({ title, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-[#1E2D44]/40">{title}</div>
      {children}
    </div>
  );
}

const muted = (text) => <p className="rounded-lg border border-dashed border-[#D2D9E3] px-3 py-4 text-center text-xs text-[#1E2D44]/45">{text}</p>;

// Leadership view of Team Workspace delivery: totals and exceptions only, no HR detail.
export function TeamDeliveryCard({ d }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canTeam = canManageTeam(user);
  const go = (link) => navigate(canTeam ? link : "/actions");
  const tiles = [
    ["open", "Open", d.open, "text-[#0B1729]", "/team?tab=work"],
    ["overdue", "Overdue", d.overdue, d.overdue ? "text-[#C95555]" : "text-[#3D8B65]", "/team?tab=work"],
    ["blocked", "Blocked", d.blocked, d.blocked ? "text-[#C95555]" : "text-[#3D8B65]", "/team?tab=work"],
    ["awaiting_review", "Awaiting review", d.awaiting_review, "text-[#B87F1E]", "/team?tab=approvals"],
    ["completed_week", "Done this week", d.completed_week, "text-[#3D8B65]", "/team?tab=work"],
    ["needs_linking", "Need linking", d.needs_linking, d.needs_linking ? "text-[#B87F1E]" : "text-[#3D8B65]", null],
  ];
  return (
    <div className="amden-card p-6" data-testid="card-team-delivery">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Icons.Network size={17} className="text-[#FFB000]" strokeWidth={2} />
          <div>
            <h3 className="font-display text-base font-bold text-[#0B1729]">Team delivery</h3>
            <p className="text-xs text-[#1E2D44]/50">{d.people_active} people active · {d.people_invited} invited · linked work only</p>
          </div>
        </div>
        {canTeam && <button onClick={() => navigate("/team")} className="text-xs font-medium text-[#FFB000] hover:underline" data-testid="open-team-link">Open Team</button>}
      </div>
      <div className="mb-6 grid grid-cols-3 gap-2 md:grid-cols-6">
        {tiles.map(([k, label, v, tone, link]) => (
          <button key={k} onClick={() => (link ? go(link) : navigate("/actions?scope=needs_link"))} data-testid={`td-${k}`}
            className="rounded-lg bg-[#F7F9FC] p-3 text-left transition-colors hover:bg-[#EEF1F6]">
            <div className={`font-display text-xl font-bold ${tone}`}>{v}</div>
            <div className="text-[11px] text-[#1E2D44]/55">{label}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Column title="Linked work by priority" testId="td-by-priority">
          {d.by_priority.length === 0 ? muted("No work is linked to a priority yet.") : (
            <div className="space-y-3">
              {d.by_priority.map((p) => (
                <div key={p.priority_id}>
                  <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                    <span className="truncate font-medium text-[#0B1729]">{p.title}</span>
                    <span className="shrink-0 text-[#1E2D44]/50">{p.completed}/{p.total}{p.blocked ? <span className="ml-1 text-[#C95555]">· {p.blocked} blocked</span> : null}</span>
                  </div>
                  <ProgressBar value={p.total ? Math.round((p.completed / p.total) * 100) : 0} color={RAG[p.blocked ? "amber" : "green"].solid} />
                </div>
              ))}
            </div>
          )}
        </Column>
        <Column title="KPI results this period" testId="td-kpis">
          {d.kpis.length === 0 ? muted("No period KPIs are being tracked.") : (
            <div className="space-y-2">
              {d.kpis.map((k) => {
                const st = PERIOD_STATE[k.state] || PERIOD_STATE.unknown;
                const v = k.verification && VERIFY_STATE[k.verification];
                return (
                  <div key={k.id} className="flex items-center justify-between gap-2 rounded-lg border border-[#EEF1F6] px-3 py-2" data-testid={`td-kpi-${k.id}`}>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-[#0B1729]">{k.name}</div>
                      <div className="truncate text-[11px] text-[#1E2D44]/45">{k.period}</div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="text-sm font-bold text-[#0B1729]">{k.value == null ? <span className="text-xs font-medium text-[#1E2D44]/45">Not reported</span> : fmtValue(k.value, k.unit)}</span>
                      <div className="flex gap-1">
                        <RagBadge rag={st.rag} className="!px-1.5 !py-0 !text-[10px]">{st.label}</RagBadge>
                        {v && <RagBadge rag={v.rag} dot={false} className="!px-1.5 !py-0 !text-[10px]">{v.label}</RagBadge>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Column>
        <Column title="Exceptions" testId="td-exceptions">
          {d.exceptions.length === 0 ? muted("No exceptions — delivery is on track.") : (
            <div className="space-y-2">
              {d.exceptions.map((x, i) => (
                <button key={i} onClick={() => go(x.link)} className="flex w-full items-center gap-2.5 rounded-lg border border-[#EEF1F6] px-3 py-2 text-left transition-colors hover:border-[#FFC24D] hover:bg-[#F4F6F9]" data-testid={`td-exception-${i}`}>
                  <span className={`h-2 w-2 shrink-0 rounded-full ${RAG[x.severity]?.dot || RAG.grey.dot}`} />
                  <span className="flex-1 text-xs text-[#1E2D44]">{x.text}</span>
                  <Icons.ChevronRight size={14} className="text-[#1E2D44]/30" />
                </button>
              ))}
            </div>
          )}
        </Column>
      </div>
    </div>
  );
}
